//

import {
  bankIdArray,
  default2FAArray,
  defaultIdArray,
  OrganizationPolicy,
  TimeEnum,
} from '@orgbrain/lib-data';
import { ALL_PERMISSIONS, SecurityGroupName } from '@orgbrain/roles';
import {
  no_requirements,
  require_logon_credentials,
  require_session_credential_period,
} from './generic-eval';
import {
  AtomicRule,
  AtomicRuleMap,
  defaultPermissionSecurityGroup,
  Policy,
  ProtoPolicyMap,
  SecurityGroupRule,
} from './org-auth-classes';

export const atomicRules: AtomicRule[] = [
  new AtomicRule(
    'session_2FA',
    {
      en: 'Session must have 2FA',
      nb: 'Session må ha 2FA',
    },
    [require_logon_credentials, require_session_credential_period],
    { credentials2FA: default2FAArray }
  ),
  new AtomicRule(
    'ID_and_session_2FA',
    {
      en: 'User must have valid ID and Session must have 2FA',
      nb: 'Bruker må ha gyldig ID og Session må ha 2FA',
    },
    [require_logon_credentials, require_session_credential_period],
    { credentials2FA: default2FAArray, credentialsID: defaultIdArray }
  ),

  new AtomicRule(
    'session_BankID',
    {
      en: 'Session must have BankID',
      nb: 'Session må ha BankID',
    },
    [require_logon_credentials, require_session_credential_period],
    { credentials2FA: bankIdArray }
  ),

  new AtomicRule(
    'basic',
    {
      en: 'No requirement',
      nb: 'Ingen krav',
    },
    [require_logon_credentials, no_requirements],
    {}
  ),
];

export const atomicRuleMap: AtomicRuleMap = atomicRules.reduce((map, obj) => {
  map[obj.name] = obj;
  return map;
}, {});

export const cannedPolicies: ProtoPolicyMap = {
  Default: {
    name: 'Default',
    description: {
      en: 'The organization has not set any specific requirements for users to use two-factor authentication (2FA). The users themselves decide whether to use 2FA.',
      nb: 'Organisasjonen har ikke satt noen bestemte krav til at brukerne må benytte tofaktorautentisering (2FA). Brukerne bestemmer selv om de skal benytte 2FA.',
    },
    securityGroupRuleMap: {
      BASIC: {
        securityGroupName: SecurityGroupName.BASIC,
        ruleName: 'basic',
      },
      MODERATE: {
        securityGroupName: SecurityGroupName.MODERATE,
        ruleName: 'basic',
      },
      HIGH: {
        securityGroupName: SecurityGroupName.HIGH,
        ruleName: 'basic',
      },
    },
    // permissionSecurityGroupMap: defaultPermissionSecurityGroup,
  },

  Admin_2FA: {
    name: 'Admin_2FA',
    description: {
      en: 'Administrator tasks will require that the user has provided 2FA credentials in the current session. 7 days inactivity requires renewal of 2FA',
      nb: 'Administratoroppgaver vil kreve at brukeren autentiserer seg med 2FA på den aktuelle enheten. 7 dager inaktivitet krever fornyelse av 2FA',
    },
    securityGroupRuleMap: {
      BASIC: {
        securityGroupName: SecurityGroupName.BASIC,
        ruleName: 'basic',
      },
      MODERATE: {
        securityGroupName: SecurityGroupName.MODERATE,
        ruleName: 'basic',
      },
      HIGH: {
        securityGroupName: SecurityGroupName.HIGH,
        ruleName: 'session_2FA',
        params: { inactivityPeriod: TimeEnum.DAY7 },
      },
    },
    // permissionSecurityGroupMap: defaultPermissionSecurityGroup,
  },

  Medium_2FA: {
    name: 'Medium_2FA',
    description: {
      en: 'Tasks that require administrator or board level access require extra authentication with 2FA credentials. 7 days inactivity requires renewal of 2FA',
      nb: 'Oppgaver som krever administatortilgang eller tilgang på styrenivå krever ekstra autentisering med 2FA. 7 dager inaktivitet krever fornyelse av 2FA',
    },
    securityGroupRuleMap: {
      BASIC: {
        securityGroupName: SecurityGroupName.BASIC,
        ruleName: 'basic',
      },
      MODERATE: {
        securityGroupName: SecurityGroupName.MODERATE,
        ruleName: 'session_2FA',
        params: { inactivityPeriod: TimeEnum.DAY7 },
      },
      HIGH: {
        securityGroupName: SecurityGroupName.HIGH,
        ruleName: 'session_2FA',
        params: { inactivityPeriod: TimeEnum.DAY7 },
      },
    },
    // permissionSecurityGroupMap: defaultPermissionSecurityGroup,
  },

  All_2FA: {
    name: 'All_2FA',
    description: {
      en: 'All access to the organization will require the user to provide 2FA credentials. 7 days inactivity requires renewal of 2FA',
      nb: 'All tilgang til denne organisasjonen krever at brukerne autentiserer seg med 2FA. 7 dager inaktivitet krever fornyelse av 2FA',
    },
    securityGroupRuleMap: {
      BASIC: {
        securityGroupName: SecurityGroupName.BASIC,
        ruleName: 'session_2FA',
        params: { inactivityPeriod: TimeEnum.DAY7 },
      },
      MODERATE: {
        securityGroupName: SecurityGroupName.MODERATE,
        ruleName: 'session_2FA',
        params: { inactivityPeriod: TimeEnum.DAY7 },
      },
      HIGH: {
        securityGroupName: SecurityGroupName.HIGH,
        ruleName: 'session_2FA',
        params: { inactivityPeriod: TimeEnum.DAY7 },
      },
    },
    // permissionSecurityGroupMap: defaultPermissionSecurityGroup,
  },

  INSANE: {
    name: 'INSANE',
    description: {
      en: 'All users must use 2FA, more sensitive tasks will require recent 2FA. Admin is required to have verified session is inactivty for 10 minutes. 12 hours for board level tasks and 7 days for common tasks',
      nb: 'Alle brukere må bruke 2FA, mer sensitive oppgaver krever nylig 2FA. Admin må ha verifisert sesjon er inaktivitet i 10 minutter. 12 timer for styretilgang og 7 dager for allminnelig tilgang',
    },
    securityGroupRuleMap: {
      BASIC: {
        securityGroupName: SecurityGroupName.BASIC,
        ruleName: 'session_2FA',
        params: { credentials2FA: default2FAArray, inactivityPeriod: TimeEnum.DAY7 },
      },
      MODERATE: {
        securityGroupName: SecurityGroupName.MODERATE,
        ruleName: 'session_2FA',
        params: { credentials2FA: default2FAArray, inactivityPeriod: TimeEnum.HOUR12 },
      },
      HIGH: {
        securityGroupName: SecurityGroupName.HIGH,
        ruleName: 'session_2FA',
        params: {
          credentials2FA: default2FAArray,
          inactivityPeriod: TimeEnum.MINUTE10,
        },
      },
    },
    // permissionSecurityGroupMap: defaultPermissionSecurityGroup,
  },

  BankID_2FA: {
    name: 'BankId_2FA',
    description: {
      en: 'All users must use BankID for 2FA.',
      nb: 'Alle brukere må bruke BankID for 2FA.',
    },
    securityGroupRuleMap: {
      BASIC: {
        securityGroupName: SecurityGroupName.BASIC,
        ruleName: 'session_BankID',
        params: { credentials2FA: bankIdArray },
      },
      MODERATE: {
        securityGroupName: SecurityGroupName.MODERATE,
        ruleName: 'session_BankID',
        params: { credentials2FA: bankIdArray }, // 1 day
      },
      HIGH: {
        securityGroupName: SecurityGroupName.HIGH,
        ruleName: 'session_BankID',
        params: {
          credentials2FA: bankIdArray,
          inactivityPeriod: TimeEnum.MINUTE30,
        }, // 1 Minute
      },
    },
    // permissionSecurityGroupMap: defaultPermissionSecurityGroup,
  },
};

//  >>> WIP
export function checkRuleContiansLower({
  rule,
  lowerRule,
}: {
  rule: SecurityGroupRule;
  lowerRule: SecurityGroupRule;
}): any {
  return rule.securityGroupName === lowerRule.securityGroupName;
}

export function orgPolicyConsistencyCheck(orgPolicy: OrganizationPolicy): any {
  const policy: Policy = createPolicy(orgPolicy);
  const basicRule = policy.securityGroupRuleMap[SecurityGroupName.BASIC];
  const moderateRule = policy.securityGroupRuleMap[SecurityGroupName.MODERATE];
  const highRule = policy.securityGroupRuleMap[SecurityGroupName.HIGH];

  const results = [];
  const tests = [
    { rule: moderateRule, lowerRule: basicRule },
    { rule: highRule, lowerRule: moderateRule },
  ];

  for (const test of tests) {
    const result = checkRuleContiansLower(test);
    if (result) {
      results.push({ test, result });
    }
  }
  return results.length === 0 ? null : results;
}

// <<< WIP

export function createPolicy(orgPolicy: OrganizationPolicy): Policy {
  const policy: Policy = structuredClone(cannedPolicies[orgPolicy.cannedPolicyName]);
  if (!policy) {
    throw new Error(`Canned policy ${orgPolicy.cannedPolicyName} not found`);
  }
  policy.permissionSecurityGroupMap = defaultPermissionSecurityGroup; // Initiliaze with default values
  policy.overrides = orgPolicy.overrides || {};

  // Override the permissionSecurityGroupMap with the values from the organization policy
  if (orgPolicy.permisionSecurityGroupOverrideMap) {
    for (const perm of ALL_PERMISSIONS) {
      policy.permissionSecurityGroupMap[perm] =
        orgPolicy.permisionSecurityGroupOverrideMap[perm] ||
        policy.permissionSecurityGroupMap[perm];
    }
  }

  // Do the overrides so the end up in the atomic rules params

  for (const [key, rule] of Object.entries(policy.securityGroupRuleMap)) {
    rule.ruleName = policy.overrides[rule.securityGroupName]?.ruleName || rule.ruleName;

    const params = atomicRuleMap[rule.ruleName].params;

    if (params) {
      rule.params = Object.assign({}, params, rule.params || {});
    } else {
      rule.params = rule.params || {};
    }

    if (policy.overrides[rule.securityGroupName]) {
      rule.params = Object.assign(rule.params, policy.overrides[rule.securityGroupName].params);
    }

    if (!params.assertLoginCredentials && orgPolicy.assertLoginCredentials) {
      rule.params = rule.params || {};
      rule.params.assertLoginCredentials = orgPolicy.assertLoginCredentials;
    }
  }

  return policy;
}
